import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { Container, Card, Button, Spinner } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

import Header_Company from "../../Components/Header_Company";
import Header_Supervisor from "../../Components/Header_Supervisor";
import { BASE_URL } from "../../Config";
import AssignPackageModal from "../../Components/CompanyDetails/AssignPackageModal";
import "./Company.css";

const AgentPackagesView = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [userRole, setuserRole] = useState(false);

  useEffect(() => {
    const storedUserData = sessionStorage.getItem("user");
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        setUserId(userData.companyId);
        setuserRole(userData.userRole);
      } catch (error) {
        Swal.fire("Error", "Failed to parse user data.", "error");
      }
    } else {
      Swal.fire("Session Expired", "Please log in again.", "warning");
    }
  }, []);

  const fetchPackages = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}AgentPackages?id=${userId}`, {
        headers: { accept: "*/*" },
      });

      const res = response.data;

      if (res.responseCode === "0000") {
        setPackages(res.data || []);
      } else if (res.responseCode === "0003") {
        Swal.fire("No Packages Found", res.message, "info");
      } else if (res.responseCode === "0004") {
        Swal.fire("Error", res.message, "error");
      } else {
        Swal.fire("Unexpected Response", "Unrecognized response code.", "warning");
      }
    } catch (error) {
      console.error("Error fetching packages", error);
      Swal.fire("Error", "An error occurred while fetching packages.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [userId]);

  const handleEditPackage = async (pkgId) => {
    try {
      const response = await axios.post(
        `${BASE_URL}EditAgentPackages?pkgId=${pkgId}`,
        {},
        { headers: { accept: "*/*" } }
      );

      const res = response.data;

      if (res.responseCode === "0000") {
        Swal.fire("Success", res.message || "Package updated successfully.", "success");
        fetchPackages();
      } else {
        Swal.fire("Error", res.message || "Something went wrong.", "error");
      }
    } catch (error) {
      console.error("Error editing package", error);
      const errorMessage =
        error.response?.data?.message || "An error occurred while editing the package.";
      Swal.fire("Error", errorMessage, "error");
    }
  };

  const columns = [
    {
      name: "Agent Name",
      selector: (row) => row.agentName,
      sortable: true,
      wrap: true,
    },
    {
      name: "Package Name",
      selector: (row) => row.packageName,
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => (
        <span className={`badge ${row.status === "Active" ? "text-bg-success" : "text-bg-danger"}`}>
          {row.status}
        </span>
      ),
    },
    {
      name: "Price",
      selector: (row) => row.packagePrice,
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <Button
          size="sm"
          variant={row.status === "Active" ? "outline-danger" : "outline-success"}
          onClick={() => handleEditPackage(row.id)}
        >
          <i
            className={`bi ${
              row.status === "Active" ? "bi-toggle-off" : "bi-toggle-on"
            } me-1`}
          />
          {row.status === "Active" ? "Deactivate" : "Activate"}
        </Button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  return (
    <div className="co-page">
      {userRole === "Company" ? <Header_Company /> : <Header_Supervisor />}

      <Container fluid="xl">
        <div className="co-hero d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <h2>
              <i className="bi bi-puzzle me-2" />
              Agent Packages
            </h2>
          <p>Assign and manage packages for your agents.</p>
          </div>
          <Button variant="light" onClick={() => setModalIsOpen(true)}>
            <i className="bi bi-plus-lg me-1" />
            Assign Package
          </Button>
        </div>

        <Card className="co-card">
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0">
              <i className="bi bi-list-check me-2 text-primary" />
              Assigned Packages
            </h5>
            <span className="badge text-bg-light border">{packages.length} total</span>
          </Card.Header>

          <Card.Body>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-2 mb-0">Loading packages...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={packages}
                  pagination
                  highlightOnHover
                  responsive
                  striped
                  noDataComponent={
                    <div className="py-4 text-muted">
                      <i className="bi bi-inbox fs-3 d-block mb-2" />
                      No packages available
                    </div>
                  }
                />
              </div>
            )}
          </Card.Body>
        </Card>

        {modalIsOpen && (
          <AssignPackageModal
            isOpen={modalIsOpen}
            onRequestClose={() => setModalIsOpen(false)}
            userId={userId}
          />
        )}
      </Container>
    </div>
  );
};

export default AgentPackagesView;
